/*
 * ⚠️⚠️⚠️ CRITICAL WARNING - PROTECTED CODE ⚠️⚠️⚠️
 * 
 * This OneSignal implementation is WORKING and STABLE after extensive debugging.
 * DO NOT MODIFY without reading /app/PUSH_NOTIFICATION_PROTECTION_WARNING.md
 * 
 * CRITICAL IMPLEMENTATION DETAILS:
 * 1. Order of operations MUST be: login(userId) → THEN → optIn()
 * 2. Use OneSignal v16 API properties (NOT v15 methods)
 * 3. External User ID must be set BEFORE subscription
 * 4. Foreground notifications are suppressed via event.preventDefault()
 * 
 * Breaking this order will cause subscriptions to become invalid!
 * See documentation for full technical details.
 * 
 * Last working: 2025-11-28
 * Modified by: E1 Agent (Fork from previous session)
 * ⚠️⚠️⚠️ END WARNING ⚠️⚠️⚠️
 */

// OneSignal Push Notification Service
import api from './api';
import { supabase } from './supabase';

/**
 * Wait for OneSignal to be ready
 */
const waitForOneSignal = () => {
  return new Promise((resolve) => {
    if (window.OneSignal) {
      resolve(window.OneSignal);
    } else {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push((OneSignal) => {
        resolve(OneSignal);
      });
    }
  });
};

/**
 * Check if push notifications are supported
 */
export const isPushNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Get current notification permission
 */
export const getNotificationPermission = () => {
  if (typeof Notification === 'undefined') return 'default';
  return Notification.permission;
};

/**
 * Check if user is subscribed to push notifications
 */
export const isPushSubscribed = async () => {
  try {
    const OneSignal = await waitForOneSignal();
    const isOptedIn = OneSignal.User.PushSubscription.optedIn;
    return isOptedIn;
  } catch (error) {
    console.error('[OneSignal] Error checking subscription:', error);
    return false;
  }
};

/**
 * Enable push notifications using OneSignal's slidedown prompt
 * Returns: { success: boolean, playerId?: string, error?: string }
 */
export const enableNotifications = async () => {
  try {
    console.log('[OneSignal] Starting notification enable flow...');
    
    if (!isPushNotificationSupported()) {
      return { success: false, error: 'Push notifications not supported' };
    }

    const OneSignal = await waitForOneSignal();
    
    // Check current permission state
    const currentPermission = Notification.permission;
    console.log('[OneSignal] Current permission:', currentPermission);

    // If already denied, can't proceed
    if (currentPermission === 'denied') {
      return { 
        success: false, 
        error: 'Notifications are blocked. Please enable them in your browser settings.' 
      };
    }

    // Check if already subscribed
    const isOptedIn = OneSignal.User.PushSubscription.optedIn;
    if (isOptedIn) {
      console.log('[OneSignal] Already subscribed');
      const playerId = OneSignal.User.PushSubscription.id;
      
      // Ensure External User ID is set
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        const currentExternalId = OneSignal.User.externalId;
        if (!currentExternalId || currentExternalId !== session.user.id) {
          console.log('[OneSignal] Setting External User ID for existing subscription:', session.user.id);
          await OneSignal.login(session.user.id);
        }
      }
      
      // Save to backend
      if (playerId) {
        await saveSubscriptionToBackend(playerId);
      }
      
      return { success: true, playerId };
    }

    // Get user session FIRST before subscribing
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      console.error('[OneSignal] No user session found. User must be logged in to subscribe.');
      return { success: false, error: 'User must be logged in to enable notifications' };
    }

    // ⚠️⚠️⚠️ CRITICAL SECTION - DO NOT MODIFY ORDER ⚠️⚠️⚠️
    // CRITICAL: Set External User ID BEFORE subscribing
    // This ensures the subscription is immediately linked to the user
    // ORDER MUST BE: 1. login(userId) → 2. optIn()
    // Reversing this order will break subscriptions!
    console.log('[OneSignal] Setting External User ID before subscription:', session.user.id);
    try {
      await OneSignal.login(session.user.id);
      console.log('[OneSignal] ✅ External User ID set');
    } catch (loginError) {
      console.error('[OneSignal] Failed to set External User ID:', loginError);
      return { success: false, error: 'Failed to identify user in OneSignal' };
    }

    // Now trigger the opt-in flow with permission prompt
    console.log('[OneSignal] Triggering permission prompt and subscription...');
    
    // Listen for subscription change to get Player ID
    return new Promise((resolve) => {
      let timeoutId;
      
      // Set up subscription change listener
      OneSignal.User.PushSubscription.addEventListener('change', async (change) => {
        console.log('[OneSignal] Subscription changed:', change);
        
        if (change.current.id && change.current.optedIn) {
          clearTimeout(timeoutId);
          const playerId = change.current.id;
          console.log('[OneSignal] ✅ Successfully subscribed, Player ID:', playerId);
          
          // Verify the linkage (v16 API uses properties, not methods)
          const externalId = OneSignal.User.externalId;  // ✅ Correct v16
          const token = OneSignal.User.PushSubscription.token;  // ✅ Correct v16
          console.log('[OneSignal] Verification - External ID:', externalId, 'Token:', token);
          
          // Save to backend
          await saveSubscriptionToBackend(playerId);
          
          resolve({ success: true, playerId });
        }
      });
      
      // ⚠️ CRITICAL: Start opt-in AFTER login was called above
      OneSignal.User.PushSubscription.optIn();
      // ⚠️⚠️⚠️ END CRITICAL SECTION ⚠️⚠️⚠️
      
      // Timeout after 15 seconds
      timeoutId = setTimeout(async () => {
        const isNowOptedIn = OneSignal.User.PushSubscription.optedIn;
        const playerId = OneSignal.User.PushSubscription.id;
        const finalPermission = Notification.permission;
        const externalId = OneSignal.User.externalId;
        
        console.log('[OneSignal] Timeout - opted in:', isNowOptedIn, 'Player ID:', playerId, 'permission:', finalPermission, 'External ID:', externalId);
        
        if (isNowOptedIn && playerId) {
          // External User ID should already be set (we called login before optIn)
          // Just save to backend
          await saveSubscriptionToBackend(playerId);
          resolve({ success: true, playerId });
        } else if (finalPermission === 'denied') {
          resolve({ success: false, error: 'Notification permission denied' });
        } else {
          resolve({ success: false, error: 'Notification permission not granted or Player ID not available' });
        }
      }, 15000);
    });
  } catch (error) {
    console.error('[OneSignal] Error enabling notifications:', error);
    return { success: false, error: error?.message || 'Unknown error occurred' };
  }
};

/**
 * Save OneSignal Player ID to backend
 */
const saveSubscriptionToBackend = async (playerId) => {
  try {
    // Get user from Supabase session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user?.id || !session?.access_token) {
      console.warn('[OneSignal] No user session or access token found, skipping backend save');
      return;
    }

    const userId = session.user.id;
    console.log('[OneSignal] Saving subscription for user:', userId, 'Player ID:', playerId);

    const response = await api.post('/api/onesignal/subscribe', {
      user_id: userId,
      player_id: playerId
    }, {
      headers: { Authorization: `Bearer ${session.access_token}` }
    });

    if (response.data?.success) {
      console.log('[OneSignal] ✅ Subscription saved to backend');
    } else {
      console.error('[OneSignal] Failed to save subscription:', response.data);
    }
  } catch (error) {
    console.error('[OneSignal] Error saving to backend:', error?.response?.data || error?.message || error);
  }
};

/**
 * Sync existing OneSignal subscription to backend
 * Call this on app startup for logged-in users
 */
export const syncExistingSubscription = async () => {
  try {
    console.log('[OneSignal] Checking for existing subscription to sync...');
    
    // Check if user is logged in
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      console.log('[OneSignal] No user session, skipping sync');
      return;
    }

    const OneSignal = await waitForOneSignal();
    
    // Check if user has an active subscription
    const isOptedIn = OneSignal.User.PushSubscription.optedIn;
    const playerId = OneSignal.User.PushSubscription.id;
    
    console.log('[OneSignal] Sync check - OptedIn:', isOptedIn, 'Player ID:', playerId);
    
    if (isOptedIn && playerId) {
      // Set External User ID if not already set
      const currentExternalId = OneSignal.User.externalId;
      console.log('[OneSignal] Current External ID:', currentExternalId);
      
      if (!currentExternalId || currentExternalId !== session.user.id) {
        console.log('[OneSignal] Setting External User ID during sync:', session.user.id);
        try {
          await OneSignal.login(session.user.id);
          console.log('[OneSignal] ✅ External User ID set to:', session.user.id);
        } catch (loginError) {
          console.error('[OneSignal] Error setting External User ID during sync:', loginError);
        }
      } else {
        console.log('[OneSignal] External User ID already correct');
      }
      
      // Save to backend
      console.log('[OneSignal] Syncing existing subscription to backend...');
      await saveSubscriptionToBackend(playerId);
    } else {
      console.log('[OneSignal] No active subscription found to sync');
    }
  } catch (error) {
    console.error('[OneSignal] Error syncing existing subscription:', error);
  }
};

/**
 * Setup notification event listeners
 */
export const setupNotificationListeners = (onNotificationReceived) => {
  try {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    
    window.OneSignalDeferred.push(async function(OneSignal) {
      try {
        console.log('[OneSignal] Setting up notification listeners...');
        
        // Listen for notification displayed (when user is on the page)
        OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
          console.log('[OneSignal] Foreground notification received:', event.notification);
          
          // Prevent the notification from showing when app is open
          // The app already has its own notification system (bell icon with badge)
          event.preventDefault();
          
          console.log('[OneSignal] Notification silenced (app is open)');
          
          // Still notify the callback for logging purposes, but don't show toast
          if (onNotificationReceived) {
            onNotificationReceived({
              title: event.notification.title,
              body: event.notification.body,
              data: event.notification.additionalData
            });
          }
        });

        // Listen for notification clicked (for logging only)
        // OneSignal handles navigation automatically via service worker
        OneSignal.Notifications.addEventListener('click', (event) => {
          console.log('[OneSignal] Notification clicked:', event.notification);
          console.log('[OneSignal] Click will be handled by service worker');
        });
        
        console.log('[OneSignal] ✅ Notification listeners set up');
      } catch (error) {
        console.error('[OneSignal] Error in listener setup:', error);
      }
    });
  } catch (error) {
    console.error('[OneSignal] Error setting up listeners:', error);
  }
};

/**
 * Helper functions for backward compatibility
 */
export const isNotificationSupported = isPushNotificationSupported;
export const isUserSubscribed = isPushSubscribed;
export const isFirstTimeUser = () => { try { return !localStorage.getItem('notificationPromptShown'); } catch { return true; } };
export const markPromptShown = () => { try { localStorage.setItem('notificationPromptShown', 'true'); } catch {} };
export const isStandaloneMode = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
